import { request, type IRequestConfig } from '@/api';
import type { ApiResponse } from '@/types/api';
import { ossKeyBatchManager } from '@/utils/OssKeyBatchManager';

const PREFIX = '/resource/system';

export type UploadRequestConfig = Pick<IRequestConfig, 'showErrorToast' | 'errorContext'>;

export interface OssStsVO {
  /**
   * 临时访问密钥ID
   */
  accessKeyId: string;
  /**
   * 临时访问密钥密码
   */
  accessKeySecret: string;
  /**
   * 存储桶名称
   */
  bucket: string;
  /**
   * 当前时间
   */
  currentTime: string;
  /**
   * 终端节点
   */
  endpoint?: string;
  /**
   * 过期时间
   */
  expiration: string;
  /**
   * 区域
   */
  region: string;
  /**
   * 安全令牌
   */
  securityToken: string;
}

export type OssFileBizType =
  | 'avatar' //头像
  | 'business_license' //营业执照
  | 'tiktok_catalog_upload_products_csv_file' //TikTok商品库商品上传CSV文件
  | 'asset_center_material_upload_image_file' //素材库图片
  | 'asset_center_material_upload_video_file' //素材库视频
  | 'asset_center_material_upload_audio_file' //素材库音频
  | 'other'; //其他

export const OssFileBizTypeEnum = {
  /**头像 */
  AVATAR: 'avatar',
  /** 营业执照 */
  BUSINESS_LICENSE: 'business_license',
  /** TikTok商品库商品上传CSV文件 */
  TIKTOK_CATALOG_UPLOAD_PRODUCTS_CSV_FILE: 'tiktok_catalog_upload_products_csv_file',
  /** 素材库图片 */
  ASSET_CENTER_MATERIAL_UPLOAD_IMAGE_FILE: 'asset_center_material_upload_image_file',
  /** 素材库视频 */
  ASSET_CENTER_MATERIAL_UPLOAD_VIDEO_FILE: 'asset_center_material_upload_video_file',
  /** 素材库音频 */
  ASSET_CENTER_MATERIAL_UPLOAD_AUDIO_FILE: 'asset_center_material_upload_audio_file',
  OTHER: 'other',
} as const;

/**
 * /system/oss/sts
 * 获取对象存储STS临时授权
 */
export const getStsToken = (config?: UploadRequestConfig) => {
  return request.get<OssStsVO>(`${PREFIX}/oss/sts`, config);
};

/**
 * /system/oss/url
 * 获取对象存储文件临时访问地址
 */
export const getOssFileUrl = (filePath: string) => {
  return request.get<string>(`${PREFIX}/oss/url`, {
    params: { filePath },
    allowCancel: false,
  });
};

/**
 * /system/oss/url/batch
 * 批量获取对象存储文件临时访问地址
 */
export const getOssFileUrls = (filePaths: string[]) => {
  return request.post<string[]>(`${PREFIX}/oss/url/batch`, filePaths, { allowCancel: false });
};

/**
 * /system/oss/keys/generate
 * 批量生成对象存储文件路径
 * @param ossFileBizType 对象存储文件业务类型
 * @param fileExtList    文件后缀列表（示例：mp4、png）
 * @param debounceMs     可选，防抖窗口时长（ms）。
 *                       - 不传：直接发起请求，行为与原接口完全一致
 *                       - 传入：在窗口期内按 ossFileBizType 分组合并请求，
 * @param config        仅控制错误提示，保留原有合并和取消规则
 */
export const generateOssKeys = async (
  ossFileBizType: OssFileBizType,
  fileExtList: string[],
  debounceMs?: number,
  config?: UploadRequestConfig
) => {
  // ── 未传第三个参数：原路返回 ──
  if (debounceMs === undefined) {
    return request.post<string[]>(
      `${PREFIX}/oss/keys/generate`,
      { ossFileBizType, fileExtList },
      { ...config, allowCancel: false }
    );
  }

  // ── 防抖合并模式 ──
  ossKeyBatchManager['debounceMs'] = debounceMs;
  const res = await Promise.all(
    fileExtList.map((ext) => ossKeyBatchManager.enqueue(ossFileBizType, ext, config))
  );
  return {
    code: 0,
    data: res,
    msg: '',
  } as ApiResponse<string[]>;
};
